import React from 'react';

/** Browser intervention as returned by GET /browser-agent/interventions. */
export interface Intervention {
  commandId: string;
  sessionId: string;
  instanceId: string;
  threadId: string;
  userId: string;
  message: string;
  vncUrl: string;
  bitwardenCredentialId: string | null;
  status: 'pending' | 'active' | 'resolved';
  createdAt: number;
  /** Operator (auth0 sub) who picked this up; null while pending. */
  claimedBy: string | null;
  claimedAt: number | null;
  claimedByUserName: string | null;
}

export const formatAge = (sinceMs: number, now = Date.now()): string => {
  const mins = Math.floor((now - sinceMs) / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  return `${Math.floor(mins / 60)}h ago`;
};

/** Unpicked interventions, FIFO — the order pick-next hands them out. */
export const pendingInterventions = (list: Intervention[]) =>
  list
    .filter((i) => i.status === 'pending')
    .sort((a, b) => a.createdAt - b.createdAt);

/** Picked-up interventions, most recently claimed first. */
export const activeInterventions = (list: Intervention[]) =>
  list
    .filter((i) => i.status === 'active')
    .sort((a, b) => (b.claimedAt ?? 0) - (a.claimedAt ?? 0));

/** The signed-in operator's own picked-up interventions. */
export const myInterventions = (
  list: Intervention[],
  userId: string | undefined,
) =>
  userId ? activeInterventions(list).filter((i) => i.claimedBy === userId) : [];

export const claimerLabel = (
  intervention: Intervention,
  currentUserId?: string,
): string | null => {
  if (!intervention.claimedBy) return null;
  if (currentUserId && intervention.claimedBy === currentUserId) return 'You';
  return intervention.claimedByUserName || intervention.claimedBy;
};

interface SectionProps {
  title: string;
  interventions: Intervention[];
  variant: 'available' | 'active';
  currentUserId?: string;
  emptyText?: string;
  renderActions: (intervention: Intervention) => React.ReactNode;
  renderDetails?: (intervention: Intervention) => React.ReactNode;
}

const MonitorIcon = () => (
  <svg
    width='16'
    height='16'
    viewBox='0 0 24 24'
    fill='none'
    stroke='currentColor'
    strokeWidth='2'
    strokeLinecap='round'
    strokeLinejoin='round'
  >
    <rect x='2' y='3' width='20' height='14' rx='2' ry='2' />
    <line x1='8' y1='21' x2='16' y2='21' />
    <line x1='12' y1='17' x2='12' y2='21' />
  </svg>
);

/**
 * One block of the Human Tasks page listing browser interventions. Rendered
 * twice for admins (Available / In Progress) and once for workers (mine).
 */
const BrowserInterventionsSection = ({
  title,
  interventions,
  variant,
  currentUserId,
  emptyText,
  renderActions,
  renderDetails,
}: SectionProps) => {
  if (interventions.length === 0 && !emptyText) return null;
  return (
    <div
      className={`tasks-section interventions-section interventions-section--${variant}`}
      data-testid={`interventions-${variant}`}
    >
      <h4 className='section-title'>{title}</h4>
      {interventions.length === 0 ? (
        <div className='empty-state'>{emptyText}</div>
      ) : (
        <div className='tasks-list'>
          {interventions.map((intervention) => {
            const claimer = claimerLabel(intervention, currentUserId);
            return (
              <div
                key={intervention.commandId}
                className={`task-row intervention-task intervention-task--${intervention.status}`}
              >
                <div className='task-type-icon'>
                  <MonitorIcon />
                </div>
                <div className='task-info'>
                  <span className='task-title'>{intervention.message}</span>
                  <span className='task-meta'>
                    <span className='task-date'>
                      {formatAge(intervention.createdAt)}
                    </span>
                    {intervention.status === 'active' && claimer && (
                      <span className='task-worker'>
                        Picked up by {claimer}
                        {intervention.claimedAt
                          ? ` · ${formatAge(intervention.claimedAt)}`
                          : ''}
                      </span>
                    )}
                  </span>
                </div>
                <div className='intervention-actions'>
                  {renderActions(intervention)}
                </div>
                {renderDetails?.(intervention)}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default BrowserInterventionsSection;
